import React from "react";
import type { Omittable } from "@salestrak/ui";

import { createDialogEmitter } from "#app/lib/dialog-emitter";
import { AnalyticsContext, AutomationEvent, trackEvent } from "#app/lib/analytics";

// Lazy: the runs dialog is an automations-admin surface opened from issue
// menus, so its chunk loads on first open instead of shipping in the
// always-loaded workspace layout bundle.
const AutomationRunsDialog = React.lazy(() =>
  import("#app/components/automations/runs/automation-runs-dialog").then((m) => ({
    default: m.AutomationRunsDialog,
  })),
);

type AutomationRunsDialogPayload = Omittable<{
  issueId: string;
  issueName?: string | null;
}>;

type AutomationRunsDialogActions = {
  /** `via` names the door for `automation.open_runs`: "issue_menu",
   *  "touch_marker" or "create_toast". */
  openDialog: (payload: AutomationRunsDialogPayload, via: string) => void;
  closeDialog: () => void;
};

const automationRunsDialog = createDialogEmitter<AutomationRunsDialogPayload>("automation_runs");

/** Open the runs dialog from outside React — a toast action, a store. */
export function openAutomationRunsDialog(
  payload: AutomationRunsDialogPayload,
  via: string,
): void {
  trackEvent({
    context: AnalyticsContext.Automation,
    event: AutomationEvent.OpenRuns,
    data: { via },
  });
  automationRunsDialog.open(payload);
}

/** Get stable open/close callbacks. Works inside bottom-sheet portals. */
export function useAutomationRunsDialogActions(): AutomationRunsDialogActions {
  return React.useMemo<AutomationRunsDialogActions>(
    () => ({
      openDialog: openAutomationRunsDialog,
      closeDialog: automationRunsDialog.close,
    }),
    [],
  );
}

export function AutomationRunsDialogMount() {
  // Nothing mounts until the first open, so the lazy chunk costs nothing at
  // app load; the payload survives close so the closing animation keeps its
  // title.
  const { open, payload, everOpened } = automationRunsDialog.useDialogState();
  if (!everOpened) return null;

  return (
    <React.Suspense fallback={null}>
      <AutomationRunsDialog
        open={open}
        onClose={automationRunsDialog.close}
        issueId={payload?.issueId ?? null}
        issueName={payload?.issueName}
      />
    </React.Suspense>
  );
}
